import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useCamera } from '@/lib/camera';
import { captureFingerprint, sha256Fingerprint } from '@/lib/fingerprint';
import { runQualityGate, sampleImage } from '@/lib/quality-gate';
import { enqueueCapture } from '@/lib/offline';
import { getSessionId } from '@/lib/session';
import { readStoredWorkflow } from '@/lib/capture-workflows';

/**
 * CA-06: Rapid session (wireframes/capture-app.html #ca06). "Shoot,
 * next, shoot": the gate still runs per shot, but a failed frame queues
 * a retake chip instead of interrupting the walk, matching the note that
 * nothing blocks the camera here. Every shot still enqueues, gate result
 * attached, per SC3 note 3. Every shot joins the job in the route
 * (`/jobs/:jobId/capture/rapid`, CAPTURE_JOBS.md section 6).
 */
export function RapidSession() {
  const navigate = useNavigate();
  const { jobId = '' } = useParams();
  const capturePath = `/jobs/${encodeURIComponent(jobId)}/capture`;
  const { videoRef, ready, capturePhoto } = useCamera();
  const [saved, setSaved] = useState(0);
  const [needsRetake, setNeedsRetake] = useState(0);
  const [busy, setBusy] = useState(false);
  // Burst captures carry the job the operator chose on CA-03, so a rapid run
  // extracts for the same purpose as a single shot.
  const [workflow] = useState(readStoredWorkflow);

  // This route is reachable on its own: an installed app reopens on the screen
  // it was left on, and the rapid mode has its own URL. Without the same gate
  // the viewfinder carries, a whole burst extracts with no job named, which
  // the operator cannot tell from a burst that carries one.
  useEffect(() => {
    if (!workflow) navigate(`${capturePath}/workflow`, { replace: true });
  }, [workflow, navigate, capturePath]);

  async function shoot() {
    setBusy(true);
    try {
      const blob = await capturePhoto();
      if (!blob) return;
      const assetFingerprint = await sha256Fingerprint(blob);
      const bitmap = await createImageBitmap(blob);
      const sample = await sampleImage(bitmap);
      const qualityResult = runQualityGate(sample);

      // One shot is one capture here, so the capture's key is derived from
      // its single asset the same way a three-angle capture derives its own.
      // A burst frame is never grouped with the shot before it: the operator
      // is walking a rack, and the next frame is the next item.
      await enqueueCapture({
        captureJobId: jobId,
        mode: 'burst',
        ...(workflow ? { workflow } : {}),
        fingerprint: await captureFingerprint([assetFingerprint]),
        assets: [
          {
            fingerprint: assetFingerprint,
            contentType: blob.type || 'image/jpeg',
            bytes: blob,
          },
        ],
        sessionId: getSessionId(),
        clientQuality: qualityResult,
      });

      setSaved((n) => n + 1);
      if (!qualityResult.passed) setNeedsRetake((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="relative mx-auto aspect-[3/4] w-full max-w-sm overflow-hidden rounded-card border border-border bg-black/40 sm:max-w-[46vh] lg:max-w-[52vh]">
        <video ref={videoRef} className="size-full object-cover" muted playsInline />
      </div>

      <div className="rounded-card border border-border p-3 text-xs text-muted">
        Session: {saved} shots. {needsRetake} may need a retake.
      </div>

      <Button size="lg" disabled={!ready || busy} onClick={() => void shoot()}>
        {busy ? 'Saving...' : 'Shoot'}
      </Button>
      <Button variant="ghost" onClick={() => navigate(capturePath)}>
        End session
      </Button>
    </div>
  );
}
